"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getBlueprintStockedExtraSelectionButtonText = exports.blueprintStockedProductExtraSelectionButtonDisabled = exports.blueprintStockedProductExtraSelectionButtonSelected = exports.blueprintStockedProductSelectedWithoutExtraSelectionButtonInCategory = void 0;
/**
 * Determines whether an ingredient has been selected in the given category but doesn't have an extra selection button
 */
const blueprintStockedProductSelectedWithoutExtraSelectionButtonInCategory = function (selectedProducts, categoryName) {
    return selectedProducts.some((selectedProduct) => {
        const { product: { category: { name }, retailPrice }, quantity } = selectedProduct;
        return quantity > 0 && name === categoryName && retailPrice === 0;
    });
};
exports.blueprintStockedProductSelectedWithoutExtraSelectionButtonInCategory = blueprintStockedProductSelectedWithoutExtraSelectionButtonInCategory;
/**
 * Determines whether an ingredient's add extra/double button is selected
 */
const blueprintStockedProductExtraSelectionButtonSelected = function (salesOrderItemBuilt, blueprintStockedProduct) {
    // get data necessary to determine state
    const { productBlueprint, selectedProducts } = salesOrderItemBuilt;
    const { productAvailabilityMap, blueprintStockedProductMinMaxQuantities } = productBlueprint;
    const { productId, retailPrice, category: { name, hasAddButtons, hasDoubleSelectionButtons } } = blueprintStockedProduct;
    const maxQuantity = blueprintStockedProductMinMaxQuantities.get(productId)
        .max;
    const selectedProduct = selectedProducts.find(sp => sp.product.productId === productId);
    const quantity = selectedProduct === undefined ? 0 : selectedProduct.quantity;
    const availability = productAvailabilityMap.get(productId);
    const { extraPrice, isAlwaysExtra, isNeverExtra } = availability;
    // add extra button
    if (hasAddButtons) {
        const hasCategoryRestrictionRule = productBlueprint.hasCategoryRestrictionRule(name);
        const categoryMainSelection = salesOrderItemBuilt.getCategoryMainSelection(name);
        // button is selected when:
        //  - ingredient is now an extra
        //  - ingredient is always an extra and at max selections allowed
        //  - current selected ingredient is not the main selection in a category
        const isNowExtra = !isAlwaysExtra && extraPrice > 0;
        if (isNowExtra) {
            return true;
        }
        const alwaysExtraAndAtMaxQuantity = isAlwaysExtra && quantity === maxQuantity;
        if (alwaysExtraAndAtMaxQuantity) {
            return true;
        }
        const currentSelectedIngredientIsNotMainSelectionInCategory = hasCategoryRestrictionRule &&
            categoryMainSelection !== null &&
            categoryMainSelection.productId !== productId &&
            quantity > 0;
        if (currentSelectedIngredientIsNotMainSelectionInCategory) {
            return true;
        }
        return false;
    }
    // double selection button
    if (hasDoubleSelectionButtons) {
        const canNeverBeSelectedMoreThanOnce = productBlueprint.noExtraSelectionForProduct(productId);
        const doubleSelectionNotAllowed = isNeverExtra || canNeverBeSelectedMoreThanOnce || !retailPrice;
        // button is selected when:
        //  - ingredient selection has reached max quantity
        //  - double selection is allowed for this ingredient
        return quantity === maxQuantity && !doubleSelectionNotAllowed;
    }
    return false;
};
exports.blueprintStockedProductExtraSelectionButtonSelected = blueprintStockedProductExtraSelectionButtonSelected;
/**
 * Determines whether an ingredient's add extra/double button is disabled
 */
const blueprintStockedProductExtraSelectionButtonDisabled = function (salesOrderItemBuilt, blueprintStockedProduct) {
    // get data necessary to determine state
    const { productBlueprint, selectedProducts } = salesOrderItemBuilt;
    const { productAvailabilityMap, blueprintStockedProductMinMaxQuantities } = productBlueprint;
    const { productId, retailPrice, category: { name, hasAddButtons, hasDoubleSelectionButtons } } = blueprintStockedProduct;
    const quantitySelected = salesOrderItemBuilt.quantitySelectedForProductBasedOnCategory(blueprintStockedProduct);
    const availability = productAvailabilityMap.get(productId);
    const { isNowExtra, extraPrice, isNeverExtra } = availability;
    // add extra button
    if (hasAddButtons) {
        const buttonSelected = (0, exports.blueprintStockedProductExtraSelectionButtonSelected)(salesOrderItemBuilt, blueprintStockedProduct);
        const productSelectedWithoutAddButton = (0, exports.blueprintStockedProductSelectedWithoutExtraSelectionButtonInCategory)(selectedProducts, name);
        const hasCategoryRestrictionRule = productBlueprint.hasCategoryRestrictionRule(name);
        const maxQuantity = blueprintStockedProductMinMaxQuantities.get(productId)
            .max;
        // button is dsiabled when:
        //  - ingredient is not now an extra
        //  - ingredient is not selected
        //  - ingredient is now an extra and its selected at maximum allowed
        //  - category has an ingredient selected where it doesn't have an add extra button
        //  - maximum selections in category have been reached and this ingredient button is not selected
        if (!isNowExtra) {
            return true;
        }
        const notSelected = quantitySelected === 0;
        if (notSelected) {
            return true;
        }
        const isNowExtraAndAtMaxSelections = extraPrice === 0 && quantitySelected === maxQuantity;
        if (isNowExtraAndAtMaxSelections) {
            return true;
        }
        const otherProductSelectedInCategoryWithoutAddExtraButton = hasCategoryRestrictionRule && productSelectedWithoutAddButton;
        if (otherProductSelectedInCategoryWithoutAddExtraButton) {
            return true;
        }
        const categoryMexSelectionReachedAndButtonNotSelected = hasCategoryRestrictionRule &&
            quantitySelected === maxQuantity &&
            !buttonSelected;
        if (categoryMexSelectionReachedAndButtonNotSelected) {
            return true;
        }
        return false;
    }
    // double selection button
    if (hasDoubleSelectionButtons) {
        const canNeverBeSelectedMoreThanOnce = productBlueprint.noExtraSelectionForProduct(productId);
        const doubleSelectionNotAllowed = isNeverExtra || canNeverBeSelectedMoreThanOnce || !retailPrice;
        return quantitySelected === 0 || doubleSelectionNotAllowed;
    }
    return false;
};
exports.blueprintStockedProductExtraSelectionButtonDisabled = blueprintStockedProductExtraSelectionButtonDisabled;
/**
 * What text to show for the different types of extra selection buttons
 */
const getBlueprintStockedExtraSelectionButtonText = function (salesOrderItemBuilt, blueprintStockedProduct) {
    // get data necessary to determine state
    const { productBlueprint } = salesOrderItemBuilt;
    const { productAvailabilityMap } = productBlueprint;
    const { title, productId, category: { name, hasAddButtons, hasDoubleSelectionButtons } } = blueprintStockedProduct;
    const categoryMainSelection = salesOrderItemBuilt.getCategoryMainSelection(name);
    const availability = productAvailabilityMap.get(productId);
    const { isNowExtra, isAlwaysExtra, complementaryCount } = availability;
    // add extra button
    if (hasAddButtons) {
        const hasCategoryPricingRule = productBlueprint.hasCategoryPricingRule(name);
        // button text is to double when:
        //  - ingredient is the main selection
        //  - ingredient is always an extra and is a single selection
        //  - ingredient is now extra and is selected
        // otherwise it will be to add
        const isMainSelection = categoryMainSelection !== null &&
            categoryMainSelection.productId === productId;
        const alwaysExtraAndIndividualSelection = isAlwaysExtra && !hasCategoryPricingRule;
        const nowExtraAndSelected = isNowExtra && complementaryCount > 0;
        const actionText = isMainSelection ||
            alwaysExtraAndIndividualSelection ||
            nowExtraAndSelected
            ? "Extra"
            : "Add";
        return `${actionText} ${title}`;
    }
    // double selection button
    if (hasDoubleSelectionButtons) {
        const buttonDisabled = (0, exports.blueprintStockedProductExtraSelectionButtonDisabled)(salesOrderItemBuilt, blueprintStockedProduct);
        // show product name only when button is not disabled and can be toggled
        return `Extra ${buttonDisabled ? "Selection" : title}`;
    }
    return null;
};
exports.getBlueprintStockedExtraSelectionButtonText = getBlueprintStockedExtraSelectionButtonText;
//# sourceMappingURL=extraSelectionButtons.js.map